import React, { useMemo, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, Search, History, Loader2, Send, Check } from 'lucide-react';
import { Batch, Portal, PORTAL_REQUIRED_FIELDS, getMissingFields, Product } from './mockData';
interface TransferPageProps {
  batches: Batch[];
  products: Product[];
  onTransfer: (portal: Portal, batchIds: string[], timestamp: string) => void;
  onBack: () => void;
  /** Navigate to the detailed view of a single batch (Level 3). */
  onOpenBatch?: (batch: Batch) => void;
}
const PORTAL_META: Record<Portal, {
  label: string;
  description: string;
  color: string;
  logo: string;
  /** Hex background that matches the logo's natural surface */
  logoBg: string;
}> = {
  ATC: {
    label: 'ATC',
    description: 'ALDI Transparenz Code',
    color: '#2563EB',
    logo: 'https://cdn.magicpatterns.com/uploads/c1H8vbR6pJAtsEyV3XMBpz/ALDI_Nord_Logo_2015.png',
    logoBg: '#FFFFFF'
  },
  OSapiens: {
    label: 'osapiens',
    description: 'Lieferketten-Sorgfaltspflichten',
    color: '#7C3AED',
    logo: 'https://cdn.magicpatterns.com/uploads/sqXwXiUP3H7hGijPWzHqLk/retina-1690354994.png',
    logoBg: '#1F2937'
  }
};
type Step = 1 | 2 | 3;
type View = 'wizard' | 'history';
export function TransferPage({
  batches,
  products,
  onTransfer,
  onBack,
  onOpenBatch
}: TransferPageProps) {
  const [view, setView] = useState<View>('wizard');
  const [step, setStep] = useState<Step>(1);
  const [portal, setPortal] = useState<Portal | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [success, setSuccess] = useState(false);
  const productNameById = useMemo(() => Object.fromEntries(products.map(p => [p.id, p.name])), [products]);
  // Only completed batches that have NOT yet been transferred to the selected portal.
  const transferableBatches = useMemo(() => {
    if (!portal) return [];
    return batches.filter(b => b.status === 'Abgeschlossen' && !b.transfers?.[portal]?.transferred);
  }, [batches, portal]);
  const nonCompletedCount = useMemo(() => batches.filter(b => b.status !== 'Abgeschlossen').length, [batches]);
  // Apply search filter
  const filteredTransferable = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return transferableBatches;
    return transferableBatches.filter(b => {
      const productName = (productNameById[b.productId] ?? '').toLowerCase();
      return b.batchNumber.toLowerCase().includes(term) || productName.includes(term) || b.productionDate.toLowerCase().includes(term);
    });
  }, [transferableBatches, search, productNameById]);
  // Validation per batch for the current portal
  const validation = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!portal) return map;
    for (const b of transferableBatches) {
      map.set(b.id, getMissingFields(b, portal));
    }
    return map;
  }, [transferableBatches, portal]);
  const selectableIds = useMemo(() => filteredTransferable.filter(b => (validation.get(b.id)?.length ?? 0) === 0).map(b => b.id), [filteredTransferable, validation]);
  const selectedValid = useMemo(() => Array.from(selected).filter(id => selectableIds.includes(id)), [selected, selectableIds]);
  const allSelectableSelected = selectableIds.length > 0 && selectableIds.every(id => selected.has(id));
  // History (already transferred)
  const historyBatches = useMemo(() => {
    return batches.filter(b => b.transfers?.ATC?.transferred || b.transfers?.OSapiens?.transferred).sort((a, b) => {
      const ta = a.transfers?.ATC?.transferredAt || a.transfers?.OSapiens?.transferredAt || '';
      const tb = b.transfers?.ATC?.transferredAt || b.transfers?.OSapiens?.transferredAt || '';
      return tb.localeCompare(ta);
    });
  }, [batches]);
  const toggleOne = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (allSelectableSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableIds));
    }
  };
  const invalidSummary = useMemo(() => {
    const items: {
      batch: Batch;
      missing: string[];
    }[] = [];
    for (const b of filteredTransferable) {
      const missing = validation.get(b.id) ?? [];
      if (missing.length > 0) items.push({
        batch: b,
        missing
      });
    }
    return items;
  }, [filteredTransferable, validation]);
  const handleTransfer = async () => {
    if (!portal || selectedValid.length === 0) return;
    setTransferring(true);
    const now = new Date();
    const ts = `${String(now.getDate()).padStart(2, '0')}.${String(now.getMonth() + 1).padStart(2, '0')}.${now.getFullYear()}, ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    await new Promise(r => setTimeout(r, 5000));
    onTransfer(portal, selectedValid, ts);
    setTransferring(false);
    setSuccess(true);
  };
  const resetWizard = () => {
    setStep(1);
    setPortal(null);
    setSelected(new Set());
    setSearch('');
    setSuccess(false);
  };
  return <div className="min-h-screen bg-[#F8FAFC] font-sans text-gray-900 pb-20" data-id="element-313">
      {/* Header — compact, single-row */}
      <header className="bg-white/85 backdrop-blur-md border-b border-gray-200 sticky top-0 z-30" data-id="element-314">
        <div className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 mx-auto py-2.5 flex items-center gap-4" data-id="element-315">
          {/* Left: back + title */}
          <button onClick={onBack} className="p-1.5 -ml-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition-colors shrink-0" aria-label="Zurück" data-id="element-316">
            <ArrowLeft className="w-4 h-4" data-id="element-317" />
          </button>
          <div className="flex items-center gap-2 shrink-0" data-id="element-318">
            <div className="w-7 h-7 rounded-md bg-[#E87722]/10 flex items-center justify-center" data-id="element-319">
              <Send className="w-3.5 h-3.5 text-[#E87722]" data-id="element-320" />
            </div>
            <h1 className="text-sm font-semibold text-gray-900 whitespace-nowrap" data-id="element-321">
              Chargen übertragen
            </h1>
          </div>

          {/* Middle: inline compact stepper */}
          {view === 'wizard' && !success && <div className="hidden md:flex items-center gap-1.5 mx-auto" data-id="element-322">
              {[{
            num: 1,
            label: 'Portal'
          }, {
            num: 2,
            label: 'Chargen'
          }, {
            num: 3,
            label: 'Bestätigen'
          }].map((s, i) => {
            const isActive = step === s.num;
            const isPast = step > s.num;
            return <Fragment key={s.num} data-id="element-323">
                    {i > 0 && <div className={`w-6 h-px transition-colors ${isPast || isActive ? 'bg-[#E87722]' : 'bg-gray-200'}`} data-id="element-324" />}
                    <div className="flex items-center gap-1.5" data-id="element-325">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors ${isActive ? 'bg-[#E87722] text-white ring-2 ring-[#E87722]/20' : isPast ? 'bg-[#E87722] text-white' : 'bg-gray-100 text-gray-400 border border-gray-200'}`} data-id="element-326">
                        {isPast ? <Check className="w-2.5 h-2.5" strokeWidth={3.5} data-id="element-327" /> : s.num}
                      </div>
                      <span className={`text-xs font-medium whitespace-nowrap ${isActive ? 'text-[#E87722]' : isPast ? 'text-gray-700' : 'text-gray-400'}`} data-id="element-328">
                        {s.label}
                      </span>
                    </div>
                  </Fragment>;
          })}
            </div>}

          {/* Right: history toggle */}
          <button onClick={() => setView(view === 'wizard' ? 'history' : 'wizard')} className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-[#E87722] transition-colors px-2 py-1 rounded-md hover:bg-gray-50 shrink-0" data-id="element-329">
            {view === 'wizard' ? <>
                <History className="w-3.5 h-3.5" data-id="element-330" />
                <span className="hidden sm:inline" data-id="element-331">Verlauf</span>
              </> : <>
                <Send className="w-3.5 h-3.5" data-id="element-332" />
                <span className="hidden sm:inline" data-id="element-333">Zurück zur Übertragung</span>
              </>}
          </button>
        </div>

        {/* Mobile-only stepper row */}
        {view === 'wizard' && !success && <div className="md:hidden px-4 sm:px-6 py-2 border-t border-gray-100 flex items-center justify-center gap-1.5" data-id="element-334">
            {[1, 2, 3].map(n => <div key={n} className={`h-1 rounded-full transition-all ${step === n ? 'w-8 bg-[#E87722]' : step > n ? 'w-4 bg-[#E87722]' : 'w-4 bg-gray-200'}`} data-id="element-335" />)}
            <span className="ml-2 text-[11px] font-medium text-gray-600" data-id="element-336">
              Schritt {step} von 3
            </span>
          </div>}
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 mx-auto py-8" data-id="element-337">
        {view === 'history' ? <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-338">
            <HistoryTable batches={historyBatches} productNameById={productNameById} data-id="element-339" />
          </div> : success ? <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center max-w-2xl mx-auto" data-id="element-340">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-341">
              <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-342" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-343">
              Erfolgreich übertragen
            </h2>
            <p className="text-gray-600 mb-8" data-id="element-344">
              {selectedValid.length} Charge
              {selectedValid.length === 1 ? '' : 'n'} wurden erfolgreich an{' '}
              {portal && PORTAL_META[portal].label} übermittelt.
            </p>
            <div className="flex justify-center gap-4" data-id="element-345">
              <button onClick={onBack} className="px-6 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors" data-id="element-346">
                Zurück zur Übersicht
              </button>
              <button onClick={resetWizard} className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white bg-[#E87722] hover:bg-[#d0681b] transition-colors" data-id="element-347">
                Weitere Chargen übertragen
              </button>
            </div>
          </motion.div> : <AnimatePresence mode="wait" data-id="element-348">
            {step === 1 && <motion.div key="step1" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 20
        }} transition={{
          duration: 0.2
        }} className="max-w-3xl mx-auto" data-id="element-349">
                <h2 className="text-xl font-semibold text-gray-900 mb-2 text-center" data-id="element-350">
                  An welches Portal möchten Sie übertragen?
                </h2>
                <p className="text-sm text-gray-500 text-center mb-8" data-id="element-351">
                  Wählen Sie das Ziel-Portal aus.
                </p>
                <div className="grid sm:grid-cols-2 gap-4 mb-10" data-id="element-352">
                  {(Object.keys(PORTAL_META) as Portal[]).map(p => {
              const m = PORTAL_META[p];
              const isSelected = portal === p;
              return <button key={p} onClick={() => {
                setPortal(p);
                setSelected(new Set());
              }} className={`relative text-left p-5 rounded-xl border-2 transition-all ${isSelected ? 'border-[#E87722] bg-[#E87722]/5 ring-4 ring-[#E87722]/10' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-353">
                        {isSelected && <div className="absolute top-3 right-3 w-6 h-6 bg-[#E87722] rounded-full flex items-center justify-center z-10" data-id="element-354">
                            <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-355" />
                          </div>}
                        <div className="flex items-center gap-4" data-id="element-356">
                          <div className="w-16 h-16 rounded-lg border border-gray-200 flex items-center justify-center shrink-0 overflow-hidden" style={{
                    backgroundColor: m.logoBg
                  }} data-id="element-357">
                            <img src={m.logo} alt={`${m.label} Logo`} className="max-w-[80%] max-h-[80%] object-contain" data-id="element-358" />
                          </div>
                          <div className="min-w-0" data-id="element-359">
                            <h3 className="text-lg font-bold text-gray-900 mb-0.5 truncate" data-id="element-360">
                              {m.label}
                            </h3>
                            <p className="text-sm text-gray-500 line-clamp-2" data-id="element-361">
                              {m.description}
                            </p>
                          </div>
                        </div>
                      </button>;
            })}
                </div>
                <div className="flex justify-end" data-id="element-362">
                  <button onClick={() => setStep(2)} disabled={!portal} className="px-8 py-3 rounded-lg text-sm font-semibold text-white bg-[#E87722] hover:bg-[#d0681b] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors" data-id="element-363">
                    Weiter
                  </button>
                </div>
              </motion.div>}

            {step === 2 && portal && <motion.div key="step2" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 20
        }} transition={{
          duration: 0.2
        }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col" data-id="element-364">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50/50" data-id="element-365">
                  <div className="relative w-72" data-id="element-366">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-367" />
                    <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Charge suchen…" className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E87722]/30 focus:border-[#E87722]" data-id="element-368" />
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-369">
                    <span data-id="element-370">Ziel:</span>
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-gray-200 bg-white" data-id="element-371">
                      <span className="w-5 h-5 rounded flex items-center justify-center overflow-hidden shrink-0" style={{
                  backgroundColor: PORTAL_META[portal].logoBg
                }} data-id="element-372">
                        <img src={PORTAL_META[portal].logo} alt={`${PORTAL_META[portal].label} Logo`} className="max-w-[85%] max-h-[85%] object-contain" data-id="element-373" />
                      </span>
                      <span className="font-semibold" style={{
                  color: PORTAL_META[portal].color
                }} data-id="element-374">
                        {PORTAL_META[portal].label}
                      </span>
                    </span>
                  </div>
                </div>

                <div className="max-h-[500px] overflow-y-auto" data-id="element-375">
                  <TransferTable batches={filteredTransferable} productNameById={productNameById} selected={selected} validation={validation} toggleOne={toggleOne} toggleAll={toggleAll} allSelectableSelected={allSelectableSelected} selectableCount={selectableIds.length} portal={portal} onOpenBatch={onOpenBatch} data-id="element-376" />
                </div>

                <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between" data-id="element-377">
                  <div className="text-sm text-gray-600" data-id="element-378">
                    <strong className="text-gray-900" data-id="element-379">
                      {selectedValid.length}
                    </strong>{' '}
                    von {selectableIds.length} übertragbaren Chargen ausgewählt
                  </div>
                  <div className="flex gap-3" data-id="element-380">
                    <button onClick={() => setStep(1)} className="px-6 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors" data-id="element-381">
                      Zurück
                    </button>
                    <button onClick={() => setStep(3)} disabled={selectedValid.length === 0} className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white bg-[#E87722] hover:bg-[#d0681b] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors" data-id="element-382">
                      Weiter
                    </button>
                  </div>
                </div>
              </motion.div>}

            {step === 3 && portal && <motion.div key="step3" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 20
        }} transition={{
          duration: 0.2
        }} className="max-w-2xl mx-auto" data-id="element-383">
                <div className="mb-6 text-center" data-id="element-384">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-1" data-id="element-385">
                    Bestätigen & übertragen
                  </h2>
                  <p className="text-sm text-gray-500" data-id="element-386">
                    Bitte überprüfen Sie die Auswahl vor der Übertragung.
                  </p>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-6" data-id="element-387">
                  {/* Portal hero band */}
                  <div className="relative px-6 py-5 border-b border-gray-100" style={{
              background: `linear-gradient(135deg, ${PORTAL_META[portal].color}08 0%, ${PORTAL_META[portal].color}03 100%)`
            }} data-id="element-388">
                    <div className="absolute top-0 left-0 right-0 h-1" style={{
                backgroundColor: PORTAL_META[portal].color
              }} data-id="element-389" />
                    <div className="flex items-center gap-4" data-id="element-390">
                      <div className="w-14 h-14 rounded-xl border border-gray-200 flex items-center justify-center overflow-hidden shrink-0 shadow-sm" style={{
                  backgroundColor: PORTAL_META[portal].logoBg
                }} data-id="element-391">
                        <img src={PORTAL_META[portal].logo} alt={`${PORTAL_META[portal].label} Logo`} className="max-w-[80%] max-h-[80%] object-contain" data-id="element-392" />
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-393">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-0.5" data-id="element-394">
                          Ziel-Portal
                        </div>
                        <div className="flex items-baseline gap-2" data-id="element-395">
                          <span className="text-xl font-bold" style={{
                      color: PORTAL_META[portal].color
                    }} data-id="element-396">
                            {PORTAL_META[portal].label}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5" data-id="element-397">
                          {PORTAL_META[portal].description}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Selected batches */}
                  <div className="px-6 py-5 border-b border-gray-100" data-id="element-398">
                    <div className="flex items-center justify-between mb-3" data-id="element-399">
                      <div data-id="element-400">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-401">
                          Ausgewählte Chargen
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5" data-id="element-402">
                          Werden an {PORTAL_META[portal].label} übertragen
                        </div>
                      </div>
                      <span className="inline-flex items-center justify-center min-w-[2.25rem] h-9 px-2 rounded-full bg-[#E87722]/10 text-[#E87722] text-sm font-bold" data-id="element-403">
                        {selectedValid.length}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto" data-id="element-404">
                      {selectedValid.map(id => {
                  const b = batches.find(x => x.id === id);
                  return b ? <span key={id} className="px-2 py-1 bg-gray-50 border border-gray-200 rounded-md text-[11px] font-mono font-medium text-gray-700 hover:bg-gray-100 transition-colors" data-id="element-405">
                            {b.batchNumber}
                          </span> : null;
                })}
                    </div>
                  </div>

                  {/* Warning */}
                  <div className="px-6 py-4 bg-amber-50/50 flex gap-3" data-id="element-406">
                    <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center shrink-0" data-id="element-407">
                      <AlertTriangle className="w-4 h-4 text-amber-700" data-id="element-408" />
                    </div>
                    <div className="text-xs text-amber-900 leading-relaxed" data-id="element-409">
                      <strong className="font-semibold" data-id="element-410">
                        Diese Aktion kann nicht rückgängig gemacht werden.
                      </strong>{' '}
                      Die ausgewählten Chargen werden dauerhaft als übertragen
                      markiert.
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center" data-id="element-411">
                  <button onClick={() => setStep(2)} disabled={transferring} className="px-6 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors" data-id="element-412">
                    Zurück zur Auswahl
                  </button>
                  <button onClick={handleTransfer} disabled={transferring} className="px-8 py-3 rounded-lg text-sm font-bold text-white bg-[#E87722] hover:bg-[#d0681b] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 shadow-md" data-id="element-413">
                    <Send className="w-5 h-5" data-id="element-414" />
                    Jetzt übertragen
                  </button>
                </div>
              </motion.div>}
          </AnimatePresence>}
      </main>

      {/* Fullscreen Loading Overlay */}
      <AnimatePresence data-id="element-415">
        {transferring && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm" data-id="element-416">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center max-w-sm w-full mx-4" data-id="element-417">
              <div className="w-32 h-32 mb-6 relative" data-id="element-418">
                <video src="https://cdn.magicpatterns.com/uploads/etHXtatYdHGmSjQ4jxyGm9/09%20butcher's%20shop.mp4" autoPlay loop muted playsInline className="w-full h-full object-contain" data-id="element-419" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-420">
                Daten werden übertragen
              </h3>
              <p className="text-sm text-gray-500 text-center" data-id="element-421">
                Bitte warten Sie einen Moment. Die Chargen werden sicher an das
                Ziel-Portal gesendet.
              </p>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// ---------- Validation Banner ----------
function ValidationBanner({
  items,
  portal
}: {
  items: {
    batch: Batch;
    missing: string[];
  }[];
  portal: Portal;
}) {
  const [open, setOpen] = useState(false);
  return <div className="border-b border-amber-200 bg-amber-50" data-id="element-422">
      <button onClick={() => setOpen(o => !o)} className="w-full px-6 py-3 flex items-center gap-3 text-left hover:bg-amber-100/50 transition-colors" data-id="element-423">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" data-id="element-424" />
        <div className="flex-1 text-sm" data-id="element-425">
          <span className="font-semibold text-amber-900" data-id="element-426">
            {items.length} Charge{items.length === 1 ? '' : 'n'} können nicht
            übertragen werden:
          </span>{' '}
          <span className="text-amber-800" data-id="element-427">
            Pflichtfelder für {portal} fehlen
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-amber-700 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-428" />
      </button>
      <AnimatePresence data-id="element-429">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="overflow-hidden" data-id="element-430">
            <ul className="px-6 pb-3 space-y-1.5" data-id="element-431">
              {items.map(({
            batch,
            missing
          }) => <li key={batch.id} className="text-xs text-amber-900 flex flex-wrap items-center gap-2" data-id="element-432">
                  <span className="font-mono font-semibold bg-amber-100 px-1.5 py-0.5 rounded" data-id="element-433">
                    {batch.batchNumber}
                  </span>
                  <span className="text-amber-700" data-id="element-434">→ fehlt:</span>
                  {missing.map(f => <span key={f} className="inline-flex items-center px-1.5 py-0.5 rounded bg-white border border-amber-200 text-amber-900 font-medium" data-id="element-435">
                      {f}
                    </span>)}
                </li>)}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// ---------- Transfer Table ----------
function TransferTable({
  batches,
  productNameById,
  selected,
  validation,
  toggleOne,
  toggleAll,
  allSelectableSelected,
  selectableCount,
  portal,
  onOpenBatch
}: {
  batches: Batch[];
  productNameById: Record<string, string>;
  selected: Set<string>;
  validation: Map<string, string[]>;
  toggleOne: (id: string) => void;
  toggleAll: () => void;
  allSelectableSelected: boolean;
  selectableCount: number;
  portal: Portal;
  onOpenBatch?: (batch: Batch) => void;
}) {
  if (batches.length === 0) {
    return <div className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-436">
        Keine offenen Chargen für {PORTAL_META[portal].label}.
      </div>;
  }
  return <table className="w-full" data-id="element-437">
      <thead className="bg-gray-50 sticky top-0 z-10" data-id="element-438">
        <tr className="border-b border-gray-200" data-id="element-439">
          <th className="px-6 py-2.5 text-left" data-id="element-440">
            <input type="checkbox" checked={allSelectableSelected} onChange={toggleAll} disabled={selectableCount === 0} className="rounded border-gray-300 text-[#E87722] focus:ring-[#E87722]" data-id="element-441" />
          </th>
          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-442">
            Chargen-Nr.
          </th>
          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-443">
            Produkt
          </th>
          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-444">
            Produktionsdatum
          </th>
          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-445">
            Abgeschlossen am
          </th>
          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-446">
            Auswahl
          </th>
          <th className="px-3 py-2.5 w-10" aria-label="Details öffnen" data-id="element-447" />
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100" data-id="element-448">
        {batches.map(b => {
        const missing = validation.get(b.id) ?? [];
        const isValid = missing.length === 0;
        const isSelected = selected.has(b.id);
        const canOpen = !!onOpenBatch;
        return <tr key={b.id} onClick={() => canOpen && onOpenBatch?.(b)} className={`group transition-colors ${isSelected ? 'bg-[#E87722]/5' : 'hover:bg-gray-50'} ${!isValid ? 'opacity-60' : ''} ${canOpen ? 'cursor-pointer' : ''}`} title={canOpen ? 'Charge-Details öffnen' : undefined} data-id="element-449">
              <td className="px-6 py-3" onClick={e => e.stopPropagation()} data-id="element-450">
                <input type="checkbox" checked={isSelected} onChange={() => toggleOne(b.id)} disabled={!isValid} className="rounded border-gray-300 text-[#E87722] focus:ring-[#E87722] disabled:cursor-not-allowed cursor-pointer" data-id="element-451" />
              </td>
              <td className="px-3 py-3 text-sm font-mono font-medium text-gray-900" data-id="element-452">
                {b.batchNumber}
              </td>
              <td className="px-3 py-3 text-sm text-gray-700" data-id="element-453">
                {productNameById[b.productId] ?? '—'}
              </td>
              <td className="px-3 py-3 text-sm text-gray-600 tabular-nums" data-id="element-454">
                {b.productionDate}
              </td>
              <td className="px-3 py-3 text-sm text-gray-700 tabular-nums whitespace-nowrap" data-id="element-455">
                {b.completedAt ?? '—'}
              </td>
              <td className="px-3 py-3" data-id="element-456">
                {isSelected && <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#E87722]" data-id="element-457">
                    <CheckCircle2 className="w-3.5 h-3.5" data-id="element-458" />
                    Zur Übertragung ausgewählt
                  </span>}
              </td>
              <td className="px-3 py-3 text-right" data-id="element-459">
                {canOpen && <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#E87722] transition-colors inline-block" data-id="element-460" />}
              </td>
            </tr>;
      })}
      </tbody>
    </table>;
}
// ---------- History Table ----------
function parseDdMmYyyy(s: string): Date | null {
  const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
  if (!m) return null;
  return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
}
function HistoryTable({
  batches,
  productNameById
}: {
  batches: Batch[];
  productNameById: Record<string, string>;
}) {
  const [search, setSearch] = useState('');
  const [portalFilter, setPortalFilter] = useState<'Alle' | Portal>('Alle');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const events = useMemo(() => {
    const evs: {
      batch: Batch;
      portal: Portal;
      record: {
        transferred: boolean;
        transferredAt?: string;
      };
    }[] = [];
    for (const b of batches) {
      if (b.transfers?.ATC?.transferred) {
        evs.push({
          batch: b,
          portal: 'ATC',
          record: b.transfers.ATC
        });
      }
      if (b.transfers?.OSapiens?.transferred) {
        evs.push({
          batch: b,
          portal: 'OSapiens',
          record: b.transfers.OSapiens
        });
      }
    }
    return evs.sort((a, b) => {
      const ta = a.record.transferredAt || '';
      const tb = b.record.transferredAt || '';
      return tb.localeCompare(ta);
    });
  }, [batches]);
  const filteredEvents = useMemo(() => {
    const term = search.toLowerCase();
    return events.filter(ev => {
      if (portalFilter !== 'Alle' && ev.portal !== portalFilter) return false;
      if (dateFrom || dateTo) {
        const evDate = ev.record.transferredAt ? parseDdMmYyyy(ev.record.transferredAt) : null;
        if (evDate) {
          if (dateFrom && evDate < new Date(dateFrom)) return false;
          if (dateTo && evDate > new Date(dateTo)) return false;
        }
      }
      if (term) {
        const pName = (productNameById[ev.batch.productId] || '').toLowerCase();
        if (!ev.batch.batchNumber.toLowerCase().includes(term) && !pName.includes(term)) {
          return false;
        }
      }
      return true;
    });
  }, [events, search, portalFilter, productNameById, dateFrom, dateTo]);
  if (events.length === 0) {
    return <div className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-461">
        Es wurden noch keine Chargen übertragen.
      </div>;
  }
  return <div className="flex flex-col h-full" data-id="element-462">
      <div className="p-4 border-b border-gray-200 bg-gray-50/50 flex flex-wrap items-center gap-4" data-id="element-463">
        <div className="relative w-64" data-id="element-464">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-465" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Charge oder Produkt suchen…" className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E87722]/30 focus:border-[#E87722]" data-id="element-466" />
        </div>
        <div className="flex items-center gap-2" data-id="element-467">
          <span className="text-sm text-gray-600 font-medium" data-id="element-468">Portal:</span>
          <select value={portalFilter} onChange={e => setPortalFilter(e.target.value as any)} className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E87722]/30 focus:border-[#E87722] bg-white" data-id="element-469">
            <option value="Alle" data-id="element-470">Alle Portale</option>
            <option value="ATC" data-id="element-471">ATC</option>
            <option value="OSapiens" data-id="element-472">osapiens</option>
          </select>
        </div>
        <div className="flex items-center gap-2 ml-auto" data-id="element-473">
          <span className="text-sm text-gray-600 font-medium" data-id="element-474">Zeitraum:</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E87722]/30 focus:border-[#E87722] bg-white" data-id="element-475" />
          <span className="text-sm text-gray-400" data-id="element-476">-</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E87722]/30 focus:border-[#E87722] bg-white" data-id="element-477" />
        </div>
      </div>

      {filteredEvents.length === 0 ? <div className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-478">
          Keine Übertragungen gefunden, die den Filtern entsprechen.
        </div> : <div className="overflow-auto max-h-[600px]" data-id="element-479">
          <table className="w-full" data-id="element-480">
            <thead className="bg-gray-50 sticky top-0 z-10" data-id="element-481">
              <tr className="border-b border-gray-200" data-id="element-482">
                <th className="px-6 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-483">
                  Chargen-Nr.
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-484">
                  Produkt
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-485">
                  Portal
                </th>
                <th className="px-6 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-486">
                  Übertragen am
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-487">
              {filteredEvents.map((ev, i) => {
            const m = PORTAL_META[ev.portal];
            return <tr key={`${ev.batch.id}-${ev.portal}-${i}`} className="hover:bg-gray-50 transition-colors" data-id="element-488">
                    <td className="px-6 py-3 text-sm font-mono font-medium text-gray-900" data-id="element-489">
                      {ev.batch.batchNumber}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-700" data-id="element-490">
                      {productNameById[ev.batch.productId] ?? '—'}
                    </td>
                    <td className="px-3 py-3" data-id="element-491">
                      <div className="flex items-center gap-2.5" data-id="element-492">
                        <div className="w-6 h-6 rounded border border-gray-200 flex items-center justify-center overflow-hidden shrink-0" style={{
                    backgroundColor: m.logoBg
                  }} data-id="element-493">
                          <img src={m.logo} alt={`${m.label} Logo`} className="max-w-[80%] max-h-[80%] object-contain" data-id="element-494" />
                        </div>
                        <span className="text-sm font-semibold text-gray-900" data-id="element-495">
                          {m.label}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-3" data-id="element-496">
                      <div className="flex items-center gap-2" data-id="element-497">
                        <CheckCircle2 className="w-4 h-4 text-green-600" data-id="element-498" />
                        <span className="text-sm text-gray-600 tabular-nums" data-id="element-499">
                          {ev.record.transferredAt ?? 'Erfolgreich'}
                        </span>
                      </div>
                    </td>
                  </tr>;
          })}
            </tbody>
          </table>
        </div>}
    </div>;
}
function TransferCell({
  record
}: {
  record?: {
    transferred: boolean;
    transferredAt?: string;
  };
}) {
  if (!record?.transferred) {
    return <span className="text-gray-300 text-xs" data-id="element-500">—</span>;
  }
  return <div className="flex items-center gap-1.5" data-id="element-501">
      <CheckCircle2 className="w-4 h-4 text-green-600" data-id="element-502" />
      <span className="text-xs text-gray-600 tabular-nums" data-id="element-503">
        {record.transferredAt ?? 'Übertragen'}
      </span>
    </div>;
}
function PortalChip({
  portal,
  transferred
}: {
  portal: Portal;
  transferred: boolean;
}) {
  const m = PORTAL_META[portal];
  return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${transferred ? 'bg-green-50 border-green-200 text-green-700' : 'bg-gray-50 border-gray-200 text-gray-400'}`} data-id="element-504">
      {transferred && <CheckCircle2 className="w-2.5 h-2.5" data-id="element-505" />}
      {m.label}
    </span>;
}
function StatusBadge({
  status
}: {
  status: string;
}) {
  const cls = status === 'Abgeschlossen' ? 'bg-green-100 text-green-800' : status === 'Bereit zum Verpacken' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`} data-id="element-506">
      {status}
    </span>;
}